import { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { pagesAPI } from '../../services/api'
import { LexicalEditor } from '@werkstatt/lexical-editor'

import { logger } from '@/utils/logger'

export default function PageEditor() {
  const { id } = useParams()
  const isEditMode = !!id

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    meta_description: '',
    is_published: false,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    if (isEditMode) {
      loadPage()
    }
  }, [id])

  const loadPage = async () => {
    setLoading(true)
    try {
      const response = await pagesAPI.getAllAdmin()
      const page = response.data.find(p => p.id === parseInt(id))

      if (page) {
        setFormData({
          title: page.title,
          slug: page.slug,
          content: page.content || '',
          meta_description: page.meta_description || '',
          is_published: page.is_published,
        })
      } else {
        setError('Seite nicht gefunden')
      }
    } catch (err) {
      setError('Fehler beim Laden der Seite')
      logger.error('Error loading page:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    })
  }

  const handleContentChange = (html) => {
    setFormData({
      ...formData,
      content: html,
    })
  }

  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[äöü]/g, match => ({ ä: 'ae', ö: 'oe', ü: 'ue' }[match]))
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')

    setFormData({ ...formData, slug })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess(false)
    setLoading(true)

    try {
      if (isEditMode) {
        await pagesAPI.update(id, formData)
        setSuccess(true)
        setTimeout(() => navigate('/admin/pages'), 1500)
      } else {
        await pagesAPI.create(formData)
        setSuccess(true)
        setTimeout(() => navigate('/admin/pages'), 1500)
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern der Seite')
      logger.error('Error saving page:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>{isEditMode ? 'Seite bearbeiten' : 'Neue Seite erstellen'}</h1>
        <Link to="/admin/pages" className="wc-btn wc-btn-secondary">
          Zurück zur Übersicht
        </Link>
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && (
        <div className="wc-alert wc-alert-success" style={{ marginBottom: 16 }}>
          Seite erfolgreich gespeichert! Weiterleitung...
        </div>
      )}

      <form onSubmit={handleSubmit} className="wc-card wc-elev-sm" style={{ gap: 20 }}>
        <div className="wc-field">
          <label htmlFor="title">Titel *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="wc-input"
            required
          />
        </div>

        <div className="wc-field">
          <label htmlFor="slug">URL-Slug *</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              className="wc-input"
              required
              placeholder="z.B. impressum"
              style={{ flex: 1 }}
            />
            <button type="button" onClick={generateSlug} className="wc-btn wc-btn-secondary" style={{ whiteSpace: 'nowrap' }}>
              Aus Titel generieren
            </button>
          </div>
          <p className="wc-muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
            Die Seite wird unter /{formData.slug} erreichbar sein
          </p>
        </div>

        <div className="wc-field">
          <label htmlFor="meta_description">Meta-Beschreibung (SEO)</label>
          <textarea
            id="meta_description"
            name="meta_description"
            value={formData.meta_description}
            onChange={handleChange}
            rows={3}
            className="wc-input"
            placeholder="Kurze Beschreibung für Suchmaschinen"
          />
        </div>

        <div className="wc-field">
          <label>Inhalt (Rich-Text-Editor) *</label>
          <div style={{ background: 'var(--wc-neutral-100)', borderRadius: 'var(--wc-radius-md)', padding: 4, color: '#1a1a1a' }}>
            <LexicalEditor
              value={formData.content}
              onChange={handleContentChange}
              placeholder="Seiteninhalt hier eingeben..."
              accentColor="green"
            />
          </div>
        </div>

        <label className="wc-check">
          <input
            type="checkbox"
            id="is_published"
            name="is_published"
            checked={formData.is_published}
            onChange={handleChange}
          />
          Seite veröffentlichen
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Link to="/admin/pages" className="wc-btn wc-btn-secondary">
            Abbrechen
          </Link>
          <button type="submit" disabled={loading} className="wc-btn wc-btn-primary">
            {loading ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </form>
    </div>
  )
}
